import { supabase } from "./supabase";

// Cache implementation for improved performance
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Chat cache hit for ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`💾 Chat cached data for ${key}`);
};

// Real-time subscription management
class SubscriptionManager {
  constructor() {
    this.subscriptions = new Map();
  }

  add(key, subscription) {
    // Remove existing subscription if it exists
    this.remove(key);
    this.subscriptions.set(key, subscription);
    console.log(`🔔 Added subscription: ${key}`);
  }

  remove(key) {
    const subscription = this.subscriptions.get(key);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(key);
      console.log(`🔕 Removed subscription: ${key}`);
    }
  }

  removeAll() {
    for (const [key] of this.subscriptions) {
      this.remove(key);
    }
    console.log("🧹 Removed all subscriptions");
  }

  has(key) {
    return this.subscriptions.has(key);
  }
}

class ChatService {
  constructor() {
    this.subscriptionManager = new SubscriptionManager();
  }

  // ====================
  // CONVERSATION METHODS
  // ====================

  /**
   * Get affiliate info by name/username
   */
  async getAffiliateByName(nameOrUsername) {
    try {
      console.log(`🔍 Looking up affiliate: ${nameOrUsername}`);

      // First try exact username match
      let { data, error } = await supabase
        .from("profiles")
        .select("id, username, full_name, email")
        .eq("username", nameOrUsername)
        .single();

      // If no exact username match, try full_name (case insensitive)
      if (!data || error) {
        const { data: nameData, error: nameError } = await supabase
          .from("profiles")
          .select("id, username, full_name, email")
          .ilike("full_name", `%${nameOrUsername}%`)
          .limit(1);

        if (nameData && nameData.length > 0) {
          data = nameData[0];
          error = null;
        } else {
          error = nameError;
        }
      }

      if (error || !data) {
        console.warn(`⚠️ Affiliate not found: ${nameOrUsername}`);
        return null;
      }

      console.log(`✅ Found affiliate:`, {
        id: data.id,
        username: data.username,
        name: data.full_name,
      });

      return data;
    } catch (err) {
      console.error("❌ ChatService.getAffiliateByName error:", err);
      return null;
    }
  }

  /**
   * Get or create conversation for affiliate and brand
   */
  async getOrCreateConversation(affiliateId, brand, mediaBuyerId = null) {
    try {
      const cacheKey = `conversation_${affiliateId}_${brand}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(
        `🚀 Creating/loading conversation for affiliate ${affiliateId}, brand ${brand}`
      );

      const { data, error } = await supabase.rpc("get_or_create_conversation", {
        p_affiliate_id: affiliateId,
        p_brand: brand,
        p_media_buyer_id: mediaBuyerId,
      });

      if (error) {
        console.error("❌ Conversation creation error:", error);
        throw new Error(`Failed to get conversation: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error("No conversation data returned");
      }

      const conversation = data[0];

      // Cache the result
      setCachedData(cacheKey, conversation);

      console.log(`✅ Conversation loaded:`, {
        id: conversation.conversation_id,
        brand: conversation.brand,
        isNew: conversation.is_new_conversation,
      });

      return conversation;
    } catch (err) {
      console.error("❌ ChatService.getOrCreateConversation error:", err);

      // Return safe fallback
      return {
        conversation_id: null,
        affiliate_id: affiliateId,
        brand: brand,
        error: err.message,
      };
    }
  }

  /**
   * Get affiliate's conversations (both brands)
   */
  async getAffiliateConversations(affiliateId) {
    try {
      const cacheKey = `affiliate_conversations_${affiliateId}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(`🚀 Loading conversations for affiliate ${affiliateId}`);

      const { data, error } = await supabase
        .from("chat_conversations")
        .select(
          `
          *,
          media_buyer:profiles!media_buyer_id(username, full_name, user_type)
        `
        )
        .eq("affiliate_id", affiliateId)
        .eq("status", "active")
        .order("last_message_at", { ascending: false });

      if (error) {
        console.error("❌ Error loading affiliate conversations:", error);
        throw new Error(`Failed to load conversations: ${error.message}`);
      }

      const conversations = data || [];

      // Cache the result
      setCachedData(cacheKey, conversations);

      console.log(
        `✅ Loaded ${conversations.length} conversations for affiliate`
      );

      return conversations;
    } catch (err) {
      console.error("❌ ChatService.getAffiliateConversations error:", err);
      return [];
    }
  }

  // ====================
  // MESSAGE METHODS
  // ====================

  /**
   * Mark messages as read when viewed
   * @param {string} conversationId
   * @param {string} userId - The user who is reading the messages
   */
  async markMessagesAsRead(conversationId, userId) {
    try {
      console.log(
        `📖 Marking messages as read for conversation: ${conversationId}, user: ${userId}`
      );

      // Update unread count for this user in this conversation
      const { error: conversationError } = await supabase
        .from("chat_conversations")
        .update({
          unread_count: 0,
          last_read_at: new Date().toISOString(),
        })
        .or(`affiliate_id.eq.${userId},media_buyer_id.eq.${userId}`)
        .eq("id", conversationId);

      if (conversationError) {
        console.error(
          "❌ Error updating conversation read status:",
          conversationError
        );
        console.error(
          "🚨 DATABASE COLUMNS MISSING! Run the SQL script to add unread_count and last_read_at columns!"
        );
        // Don't throw - this is not critical
      } else {
        console.log("✅ Successfully updated conversation unread_count to 0");
      }

      // Mark individual messages as read (for message-level read receipts)
      const { error: messageError } = await supabase
        .from("chat_messages")
        .update({ read_at: new Date().toISOString() })
        .eq("conversation_id", conversationId)
        .neq("sender_id", userId) // Don't mark own messages as read
        .is("read_at", null); // Only mark unread messages

      if (messageError) {
        console.error("❌ Error marking messages as read:", messageError);
        console.error(
          "🚨 MESSAGE UPDATE FAILED! This is why unread counts come back on refresh!"
        );
        // Don't throw - this is not critical
      } else {
        console.log("✅ Successfully marked individual messages as read");
      }

      // Clear relevant caches
      this.clearConversationCaches(conversationId);

      console.log(
        `✅ Messages marked as read for conversation: ${conversationId}`
      );
    } catch (err) {
      console.error("❌ Error marking messages as read:", err);
      // Don't throw - this is not critical for the chat functionality
    }
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(
    conversationId,
    senderId,
    messageText,
    messageType = "text",
    driveLink = null
  ) {
    try {
      console.log(`🚀 Sending message in conversation ${conversationId}`);

      const { data, error } = await supabase.rpc("send_chat_message", {
        p_conversation_id: conversationId,
        p_sender_id: senderId,
        p_message_text: messageText,
        p_message_type: messageType,
        p_drive_link: driveLink,
      });

      if (error) {
        console.error("❌ Message send error:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No message data returned");
      }

      const message = data[0];

      // Clear relevant caches
      this.clearConversationCaches(conversationId);

      console.log(`✅ Message sent:`, {
        id: message.message_id,
        type: message.message_type,
        submissionCreated: message.submission_created,
      });

      return message;
    } catch (err) {
      console.error("❌ ChatService.sendMessage error:", err);
      throw err; // Re-throw to show user the specific error
    }
  }

  /**
   * Get messages for a conversation
   */
  async getConversationMessages(
    conversationId,
    userId,
    limit = 50,
    offset = 0
  ) {
    try {
      const cacheKey = `messages_${conversationId}_${limit}_${offset}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(`🚀 Loading messages for conversation ${conversationId}`);

      // Try the optimized function first, fallback to direct query
      let data, error;
      try {
        const result = await supabase.rpc("get_conversation_messages", {
          p_conversation_id: conversationId,
          p_user_id: userId,
          p_limit: limit,
          p_offset: offset,
        });
        data = result.data;
        error = result.error;
      } catch (funcError) {
        console.log("⚠️ Database function failed, using direct query fallback");
        // Fallback to direct table query
        const result = await supabase
          .from("chat_messages")
          .select(
            `
            id,
            conversation_id,
            sender_id,
            message_text,
            message_type,
            drive_link,
            is_edited,
            created_at,
            edited_at,
            read_at,
            sender:profiles!sender_id(
              id,
              username,
              full_name,
              email,
              user_type
            )
          `
          )
          .eq("conversation_id", conversationId)
          .order("created_at", { ascending: true })
          .limit(limit);

        data = result.data || [];
        error = result.error;
      }

      if (error) {
        console.error("❌ Error loading messages:", error);
        throw new Error(`Failed to load messages: ${error.message}`);
      }

      const messages = data || [];

      // Cache the result (shorter cache for messages)
      cache.set(cacheKey, {
        data: messages,
        timestamp: Date.now(),
      });

      console.log(`✅ Loaded ${messages.length} messages`);

      return messages;
    } catch (err) {
      console.error("❌ ChatService.getConversationMessages error:", err);
      return [];
    }
  }

  /**
   * Edit a message (affiliates only, their own messages)
   */
  async editMessage(messageId, newText, userId) {
    try {
      console.log(`🚀 Editing message ${messageId}`);

      const { data, error } = await supabase
        .from("chat_messages")
        .update({
          message_text: newText,
          is_edited: true,
          edited_at: new Date().toISOString(),
        })
        .eq("id", messageId)
        .eq("sender_id", userId)
        .select()
        .single();

      if (error) {
        console.error("❌ Message edit error:", error);
        throw new Error(`Failed to edit message: ${error.message}`);
      }

      // Clear relevant caches
      this.clearConversationCaches(data.conversation_id);

      console.log(`✅ Message edited successfully`);

      return data;
    } catch (err) {
      console.error("❌ ChatService.editMessage error:", err);
      throw err;
    }
  }

  // ====================
  // REACTION METHODS
  // ====================

  /**
   * Get reactions for a specific message
   * @param {string} messageId
   * @returns {Array} Reactions array
   */
  async getMessageReactions(messageId) {
    try {
      console.log(`🔍 Loading reactions for message: ${messageId}`);

      const { data, error } = await supabase
        .from("message_reactions")
        .select(
          `
          id,
          message_id,
          user_id,
          reaction_type,
          created_at,
          user:profiles!user_id(
            id,
            username,
            full_name
          )
        `
        )
        .eq("message_id", messageId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("❌ Error loading message reactions:", error);
        return [];
      }

      const reactions = (data || []).map((reaction) => ({
        id: reaction.id,
        message_id: reaction.message_id,
        user_id: reaction.user_id,
        reaction_type: reaction.reaction_type,
        created_at: reaction.created_at,
        user_name:
          reaction.user?.full_name || reaction.user?.username || "Unknown User",
      }));

      console.log(`✅ Loaded ${reactions.length} reactions for message`);
      return reactions;
    } catch (err) {
      console.error("❌ Error loading message reactions:", err);
      return [];
    }
  }

  /**
   * Add reaction to a message (media buyers only)
   */
  async addReaction(messageId, userId, reactionType) {
    try {
      console.log(`🚀 Adding reaction ${reactionType} to message ${messageId}`);

      const { data, error } = await supabase.rpc("add_message_reaction", {
        p_message_id: messageId,
        p_user_id: userId,
        p_reaction_type: reactionType,
      });

      if (error) {
        console.error("❌ Reaction add error:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No reaction data returned");
      }

      const reaction = data[0];

      // Clear relevant caches
      this.clearMessageCaches(messageId);

      console.log(`✅ Reaction added:`, {
        type: reaction.reaction_type,
        submissionUpdated: reaction.submission_updated,
      });

      return reaction;
    } catch (err) {
      console.error("❌ ChatService.addReaction error:", err);
      throw err;
    }
  }

  // ====================
  // MEDIA BUYER METHODS
  // ====================

  /**
   * Get media buyer dashboard data with submission-specific conversations
   */
  async getMediaBuyerDashboard(mediaBuyerId, filters = {}) {
    try {
      const {
        brand = null,
        status = null,
        date = null,
        search = null,
      } = filters;

      const cacheKey = `dashboard_${mediaBuyerId}_${JSON.stringify(filters)}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(
        `🚀 Loading submission-specific dashboard for ${mediaBuyerId}`,
        { filters }
      );

      // Try new submission-specific function first, fallback to old one
      let data, error;
      try {
        const result = await supabase.rpc(
          "get_media_buyer_dashboard_submission_specific",
          {
            p_media_buyer_id: mediaBuyerId,
            p_brand_filter: brand || null,
            p_status_filter: status || null,
            p_date_filter: date || null,
            p_search_term: search || null,
          }
        );
        data = result.data;
        error = result.error;
      } catch (funcError) {
        console.log("⚠️ New function not found, using legacy function");
        // Fallback to old function
        const result = await supabase.rpc("get_media_buyer_dashboard", {
          p_media_buyer_id: mediaBuyerId,
          p_date_filter: date || null,
        });
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error("❌ Dashboard load error:", error);
        throw new Error(`Failed to load dashboard: ${error.message}`);
      }

      const dashboard = data || [];

      // Transform the data to match expected format (handle both new and old formats)
      const conversations = dashboard.map((row) => ({
        id: row.conversation_id || row.id,
        conversation_id: row.conversation_id || row.id,
        affiliate_id: row.affiliate_id,
        media_buyer_id: row.media_buyer_id,
        brand: row.brand,
        submission_date: row.submission_date || null,
        submission_title:
          row.submission_title ||
          `Conversation with ${row.affiliate_name || row.affiliate?.name || "Unknown"}`,
        submission_url: row.submission_url || null,
        status: row.status,
        unread_count: row.unread_count || 0,
        last_message_text: row.last_message_text,
        last_message_at: row.last_message_at,
        last_message_sender_name: row.last_message_sender_name,
        created_at: row.created_at,
        affiliate: {
          id: row.affiliate_id,
          name: row.affiliate_name || row.affiliate?.name || "Unknown",
          username:
            row.affiliate_username || row.affiliate?.username || "unknown",
          email: row.affiliate_email || row.affiliate?.email || null,
        },
      }));

      // Cache the result
      setCachedData(cacheKey, { conversations });

      console.log(
        `✅ Dashboard loaded with ${conversations.length} submission-specific conversations`
      );

      return { conversations };
    } catch (err) {
      console.error("❌ ChatService.getMediaBuyerDashboard error:", err);
      return { conversations: [] };
    }
  }

  /**
   * Get affiliate dashboard data with separate conversations per media buyer
   */
  async getAffiliateDashboard(affiliateId, filters = {}) {
    try {
      const { brand = null } = filters;

      const cacheKey = `affiliate_dashboard_${affiliateId}_${JSON.stringify(filters)}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(`🚀 Loading affiliate dashboard for ${affiliateId}`, {
        filters,
      });

      // Call the new affiliate dashboard function
      const { data, error } = await supabase.rpc("get_affiliate_dashboard", {
        p_affiliate_id: affiliateId,
        p_brand: brand || null,
      });

      if (error) {
        console.error("❌ Affiliate dashboard load error:", error);
        throw new Error(`Failed to load affiliate dashboard: ${error.message}`);
      }

      const dashboard = data || [];

      // Transform the data to match expected format
      const conversations = dashboard.map((row) => ({
        id: row.conversation_id,
        conversation_id: row.conversation_id,
        affiliate_id: affiliateId,
        media_buyer_id: row.media_buyer_id,
        brand: row.brand,
        submission_date: row.submission_date,
        submission_title:
          row.submission_title ||
          `Chat with ${row.media_buyer_name || "Media Buyer"}`,
        status: row.status,
        unread_count: row.unread_count || 0,
        last_message_at: row.last_message_at,
        created_at: row.created_at,
        media_buyer: {
          id: row.media_buyer_id,
          name: row.media_buyer_name || "Media Buyer",
          username: row.media_buyer_username || "unknown",
        },
      }));

      // Cache the result
      setCachedData(cacheKey, { conversations });

      console.log(
        `✅ Affiliate dashboard loaded with ${conversations.length} conversations`
      );

      return { conversations };
    } catch (err) {
      console.error("❌ ChatService.getAffiliateDashboard error:", err);
      return { conversations: [] };
    }
  }

  // ====================
  // UTILITY METHODS
  // ====================

  /**
   * Check daily submission status for affiliate/brand
   */
  async getDailySubmissionStatus(affiliateId, brand, date = null) {
    try {
      const submissionDate = date || new Date().toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("daily_submissions")
        .select("*")
        .eq("affiliate_id", affiliateId)
        .eq("brand", brand)
        .eq("submission_date", submissionDate)
        .order("submitted_at", { ascending: false });

      if (error) {
        console.error("❌ Submission status error:", error);
        throw new Error(`Failed to check submission status: ${error.message}`);
      }

      const submissions = data || [];
      const hasSubmittedToday = submissions.length > 0;
      const canSubmitMore = submissions.length < 1; // Max 1 per day

      return {
        submissions,
        hasSubmittedToday,
        canSubmitMore,
        submissionCount: submissions.length,
        maxSubmissions: 1,
      };
    } catch (err) {
      console.error("❌ ChatService.getDailySubmissionStatus error:", err);
      return {
        submissions: [],
        hasSubmittedToday: false,
        canSubmitMore: true,
        submissionCount: 0,
        maxSubmissions: 1,
      };
    }
  }

  /**
   * Get system settings
   */
  async getSystemSettings() {
    try {
      const cacheKey = "chat_system_settings";
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      const { data, error } = await supabase
        .from("chat_system_settings")
        .select("*");

      if (error) {
        console.error("❌ System settings error:", error);
        throw new Error(`Failed to load settings: ${error.message}`);
      }

      // Convert to key-value object
      const settings = {};
      data.forEach((setting) => {
        settings[setting.setting_key] = setting.setting_value;
      });

      setCachedData(cacheKey, settings);

      return settings;
    } catch (err) {
      console.error("❌ ChatService.getSystemSettings error:", err);
      return {
        submission_deadline_est: "22:00",
        max_daily_submissions: "1",
        auto_assign_media_buyer: "true",
        enable_file_validation: "true",
      };
    }
  }

  // ====================
  // CACHE MANAGEMENT
  // ====================

  /**
   * Clear caches related to a conversation
   */
  clearConversationCaches(conversationId) {
    const keysToDelete = [];
    for (const [key] of cache) {
      if (
        key.includes(conversationId) ||
        key.includes("messages_") ||
        key.includes("dashboard_")
      ) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => cache.delete(key));
    console.log(
      `🧹 Cleared ${keysToDelete.length} conversation-related cache entries`
    );
  }

  /**
   * Clear caches related to a message
   */
  clearMessageCaches(messageId) {
    const keysToDelete = [];
    for (const [key] of cache) {
      if (key.includes("messages_") || key.includes("dashboard_")) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => cache.delete(key));
    console.log(
      `🧹 Cleared ${keysToDelete.length} message-related cache entries`
    );
  }

  /**
   * Clear all chat caches
   */
  clearAllCaches() {
    cache.clear();
    console.log("🧹 All chat caches cleared");
  }

  /**
   * Subscribe to new messages in a conversation
   * @param {string} conversationId - Conversation ID to subscribe to
   * @param {function} onNewMessage - Callback for new messages
   * @param {function} onError - Error callback
   * @returns {object} Subscription object
   */
  subscribeToMessages(conversationId, onNewMessage, onError = null) {
    try {
      console.log(
        `🔔 Subscribing to messages for conversation: ${conversationId}`
      );

      const subscription = supabase
        .channel(`conversation_${conversationId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "chat_messages",
            filter: `conversation_id=eq.${conversationId}`,
          },
          async (payload) => {
            try {
              console.log("📨 New message received:", payload.new);

              // Fetch complete message data with sender info
              const { data: messageData, error } = await supabase
                .from("chat_messages")
                .select(
                  `
                  *,
                  sender:sender_id (
                    id,
                    username,
                    full_name,
                    email,
                    user_type
                  )
                `
                )
                .eq("id", payload.new.id)
                .single();

              if (error) {
                console.error("❌ Error fetching new message details:", error);
                return;
              }

              // Clear cache for this conversation
              this.clearConversationCaches(conversationId);

              // Call the callback with the complete message data
              onNewMessage(messageData);
            } catch (err) {
              console.error("❌ Error processing new message:", err);
              if (onError) onError(err);
            }
          }
        )
        .subscribe((status) => {
          console.log(`🔔 Message subscription status: ${status}`);
          if (status === "SUBSCRIBED") {
            console.log("✅ Successfully subscribed to messages");
          } else if (status === "CHANNEL_ERROR") {
            console.error("❌ Message subscription error");
            if (onError) onError(new Error("Message subscription failed"));
          }
        });

      // Store subscription for cleanup
      const subscriptionKey = `messages_${conversationId}`;
      this.subscriptionManager.add(subscriptionKey, subscription);

      // Return a cleanup function instead of the subscription object
      return () => {
        try {
          console.log(
            `🔌 Unsubscribing from messages for conversation ${conversationId}`
          );
          subscription.unsubscribe();
          this.subscriptionManager.remove(subscriptionKey);
        } catch (error) {
          console.warn("⚠️ Error during message subscription cleanup:", error);
        }
      };
    } catch (error) {
      console.error("❌ Error setting up message subscription:", error);
      if (onError) onError(error);
      return null;
    }
  }

  /**
   * Subscribe to message reactions in a conversation
   * @param {string} conversationId - Conversation ID to subscribe to
   * @param {function} onReactionUpdate - Callback for reaction updates
   * @param {function} onError - Error callback
   * @returns {object} Subscription object
   */
  subscribeToReactions(conversationId, onReactionUpdate, onError = null) {
    try {
      console.log(
        `🔔 Subscribing to reactions for conversation: ${conversationId}`
      );

      const subscription = supabase
        .channel(`reactions_${conversationId}`)
        .on(
          "postgres_changes",
          {
            event: "*", // Listen to all events (INSERT, UPDATE, DELETE)
            schema: "public",
            table: "message_reactions",
          },
          async (payload) => {
            try {
              console.log("👍 Reaction update received:", payload);

              // Check if this reaction belongs to a message in our conversation
              const { data: messageData, error: messageError } = await supabase
                .from("chat_messages")
                .select("conversation_id")
                .eq("id", payload.new?.message_id || payload.old?.message_id)
                .single();

              if (
                messageError ||
                messageData?.conversation_id !== conversationId
              ) {
                return; // Not for our conversation
              }

              // Clear cache for reactions
              this.clearMessageCaches(
                payload.new?.message_id || payload.old?.message_id
              );

              // Call the callback with the reaction data
              onReactionUpdate({
                event: payload.eventType,
                messageId: payload.new?.message_id || payload.old?.message_id,
                reaction: payload.new || payload.old,
              });
            } catch (err) {
              console.error("❌ Error processing reaction update:", err);
              if (onError) onError(err);
            }
          }
        )
        .subscribe((status) => {
          console.log(`🔔 Reaction subscription status: ${status}`);
          if (status === "SUBSCRIBED") {
            console.log("✅ Successfully subscribed to reactions");
          } else if (status === "CHANNEL_ERROR") {
            console.error("❌ Reaction subscription error");
            if (onError) onError(new Error("Reaction subscription failed"));
          }
        });

      // Store subscription for cleanup
      const subscriptionKey = `reactions_${conversationId}`;
      this.subscriptionManager.add(subscriptionKey, subscription);

      // Return a cleanup function instead of the subscription object
      return () => {
        try {
          console.log(
            `🔌 Unsubscribing from reactions for conversation ${conversationId}`
          );
          subscription.unsubscribe();
          this.subscriptionManager.remove(subscriptionKey);
        } catch (error) {
          console.warn("⚠️ Error during reaction subscription cleanup:", error);
        }
      };
    } catch (error) {
      console.error("❌ Error setting up reaction subscription:", error);
      if (onError) onError(error);
      return null;
    }
  }

  /**
   * Subscribe to conversation updates for media buyer dashboard
   * @param {string} mediaBuyerId - Media buyer ID
   * @param {function} onConversationUpdate - Callback for conversation updates
   * @param {function} onError - Error callback
   * @returns {object} Subscription object
   */
  subscribeToConversationUpdates(
    mediaBuyerId,
    onConversationUpdate,
    onError = null
  ) {
    try {
      console.log(
        `🔔 Subscribing to conversation updates for media buyer: ${mediaBuyerId}`
      );

      const subscription = supabase
        .channel(`dashboard_${mediaBuyerId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "chat_conversations",
            filter: `media_buyer_id=eq.${mediaBuyerId}`,
          },
          (payload) => {
            try {
              console.log("💬 Conversation update received:", payload);

              // Clear dashboard cache
              this.clearDashboardCache(mediaBuyerId);

              onConversationUpdate({
                event: payload.eventType,
                conversation: payload.new || payload.old,
              });
            } catch (err) {
              console.error("❌ Error processing conversation update:", err);
              if (onError) onError(err);
            }
          }
        )
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "chat_messages",
          },
          async (payload) => {
            try {
              // Check if this message belongs to any of the media buyer's conversations
              const { data: conversationData, error } = await supabase
                .from("chat_conversations")
                .select("id")
                .eq("media_buyer_id", mediaBuyerId)
                .eq("id", payload.new.conversation_id)
                .single();

              if (!error && conversationData) {
                console.log("📨 New message in dashboard conversation");

                // Clear dashboard cache
                this.clearDashboardCache(mediaBuyerId);

                onConversationUpdate({
                  event: "message_added",
                  conversationId: payload.new.conversation_id,
                  message: payload.new,
                });
              }
            } catch (err) {
              console.error("❌ Error processing message for dashboard:", err);
              if (onError) onError(err);
            }
          }
        )
        .subscribe((status) => {
          console.log(`🔔 Dashboard subscription status: ${status}`);
          if (status === "SUBSCRIBED") {
            console.log("✅ Successfully subscribed to dashboard updates");
          } else if (status === "CHANNEL_ERROR") {
            console.error("❌ Dashboard subscription error");
            if (onError) onError(new Error("Dashboard subscription failed"));
          }
        });

      // Store subscription for cleanup
      const subscriptionKey = `dashboard_${mediaBuyerId}`;
      this.subscriptionManager.add(subscriptionKey, subscription);

      // Return a cleanup function instead of the subscription object
      return () => {
        try {
          console.log(
            `🔌 Unsubscribing from dashboard updates for ${mediaBuyerId}`
          );
          subscription.unsubscribe();
          this.subscriptionManager.remove(subscriptionKey);
        } catch (error) {
          console.warn("⚠️ Error during subscription cleanup:", error);
        }
      };
    } catch (error) {
      console.error("❌ Error setting up dashboard subscription:", error);
      if (onError) onError(error);
      return null;
    }
  }

  /**
   * Unsubscribe from messages for a conversation
   * @param {string} conversationId - Conversation ID
   */
  unsubscribeFromMessages(conversationId) {
    const subscriptionKey = `messages_${conversationId}`;
    this.subscriptionManager.remove(subscriptionKey);
  }

  /**
   * Unsubscribe from reactions for a conversation
   * @param {string} conversationId - Conversation ID
   */
  unsubscribeFromReactions(conversationId) {
    const subscriptionKey = `reactions_${conversationId}`;
    this.subscriptionManager.remove(subscriptionKey);
  }

  /**
   * Unsubscribe from dashboard updates
   * @param {string} mediaBuyerId - Media buyer ID
   */
  unsubscribeFromDashboard(mediaBuyerId) {
    const subscriptionKey = `dashboard_${mediaBuyerId}`;
    this.subscriptionManager.remove(subscriptionKey);
  }

  /**
   * Unsubscribe from all active subscriptions
   */
  unsubscribeFromAll() {
    this.subscriptionManager.removeAll();
  }

  /**
   * Get active subscription count (for debugging)
   */
  getActiveSubscriptionCount() {
    return this.subscriptionManager.subscriptions.size;
  }

  /**
   * Clear dashboard cache for a media buyer
   */
  clearDashboardCache(mediaBuyerId) {
    const patterns = [
      `dashboard_${mediaBuyerId}`,
      `media_buyer_dashboard_${mediaBuyerId}`,
    ];
    patterns.forEach((pattern) => {
      for (const key of cache.keys()) {
        if (key.includes(pattern)) {
          cache.delete(key);
          console.log(`🧹 Cleared dashboard cache: ${key}`);
        }
      }
    });
  }

  /**
   * Clear all caches and subscriptions (cleanup method)
   */
  cleanup() {
    this.clearAllCaches();
    this.unsubscribeFromAll();
    console.log("🧹 ChatService cleanup completed");
  }
}

// Create and export the service instance
const chatService = new ChatService();

// Export the service instance
export { chatService };

// Export individual methods with proper context binding
export const getOrCreateConversation = (...args) =>
  chatService.getOrCreateConversation(...args);
export const getAffiliateByName = (...args) =>
  chatService.getAffiliateByName(...args);
export const getAffiliateConversations = (...args) =>
  chatService.getAffiliateConversations(...args);
export const sendMessage = (...args) => chatService.sendMessage(...args);
export const getConversationMessages = (...args) =>
  chatService.getConversationMessages(...args);
export const editMessage = (...args) => chatService.editMessage(...args);
export const addReaction = (...args) => chatService.addReaction(...args);
export const getMessageReactions = (...args) =>
  chatService.getMessageReactions(...args);
export const markMessagesAsRead = (...args) =>
  chatService.markMessagesAsRead(...args);
export const getMediaBuyerDashboard = (...args) =>
  chatService.getMediaBuyerDashboard(...args);
export const getAffiliateDashboard = (...args) =>
  chatService.getAffiliateDashboard(...args);
export const getDailySubmissionStatus = (...args) =>
  chatService.getDailySubmissionStatus(...args);
export const getSystemSettings = (...args) =>
  chatService.getSystemSettings(...args);
export const clearAllCaches = (...args) => chatService.clearAllCaches(...args);

// Real-time subscription methods with proper context
export const subscribeToMessages = (...args) =>
  chatService.subscribeToMessages(...args);
export const subscribeToReactions = (...args) =>
  chatService.subscribeToReactions(...args);
export const subscribeToConversationUpdates = (...args) =>
  chatService.subscribeToConversationUpdates(...args);
export const unsubscribeFromMessages = (...args) =>
  chatService.unsubscribeFromMessages(...args);
export const unsubscribeFromReactions = (...args) =>
  chatService.unsubscribeFromReactions(...args);
export const unsubscribeFromDashboard = (...args) =>
  chatService.unsubscribeFromDashboard(...args);
export const unsubscribeFromAll = (...args) =>
  chatService.unsubscribeFromAll(...args);
export const getActiveSubscriptionCount = (...args) =>
  chatService.getActiveSubscriptionCount(...args);
